import React, { useEffect, useState } from 'react';
import {
  Divider, Box, Typography, FormControl, InputLabel, MenuItem, Select, Button, Dialog, DialogActions, DialogContent
} from '@mui/material';
import { collection, query, where, getDocs, doc, getDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import SalarySlip from './SalarySlip'; // Import the SalarySlip component
import { buttonStyles, textFieldStyles } from '../../Login/LoginPageStyles';
import './printStyles.css'; // Import print-specific styles

const ESalary = ({ user }) => {
  const [salaryData, setSalaryData] = useState(null);
  const [months, setMonths] = useState([]);
  const [selectedMonth, setSelectedMonth] = useState('');
  const [currentDate, setCurrentDate] = useState('');
  const [openDialog, setOpenDialog] = useState(false);

  // Set current date in a readable format
  useEffect(() => {
    const today = new Date();
    const options = { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' };
    setCurrentDate(today.toLocaleDateString(undefined, options));
  }, []);

  // Fetch the list of months from salary documents where userId matches
  useEffect(() => {
    const fetchMonths = async () => {
      try {
        const q = query(collection(db, 'employeeSalary'), where('userId', '==', user.id));
        const querySnapshot = await getDocs(q);

        const fetchedMonths = [];
        querySnapshot.forEach((doc) => {
          fetchedMonths.push(doc.data().month); // Assuming 'month' is a field in your document
        });

        setMonths(fetchedMonths);
      } catch (error) {
        console.error('Error fetching months:', error);
      }
    };

    fetchMonths();
  }, [user.id]);

  // Fetch salary details based on selected month
  useEffect(() => {
    const fetchSalaryData = async () => {
      if (!selectedMonth) return;

      try {
        const salaryRef = doc(db, 'employeeSalary', `${user.id}-${selectedMonth}`);
        const salaryDoc = await getDoc(salaryRef);

        if (salaryDoc.exists()) {
          setSalaryData(salaryDoc.data());
        } else {
          console.error('No such document!');
        }
      } catch (error) {
        console.error('Error fetching salary details:', error);
      }
    };

    fetchSalaryData();
  }, [selectedMonth, user.id]);

  // Function to open the dialog for printing the salary slip
  const handlePrintDialog = () => {
    setOpenDialog(true);
  };

  // Function to close the dialog
  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  // Function to print the salary slip
  const handlePrint = () => {
    window.print();
  };

  if (!months.length) {
    return <Typography>Loading available months...</Typography>;
  }

  return (
    <Box sx={{ flexGrow: 1, padding: 3, paddingTop: 0, marginTop: 0 }}>
      <Box sx={{ mb: 2 }}>
        <Typography variant="h6" sx={{ color: '#39B54A' }}>
          Salary
        </Typography>
      </Box>

      <Box
        sx={{
          backgroundColor: 'white',
          borderRadius: 2,
          padding: 3,
          mb: 3,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-start',
        }}
      >
        {/* Dropdown for selecting the month */}
        <FormControl fullWidth sx={{...textFieldStyles, flex: 1, mr: 2 }}>
          <InputLabel id="month-select-label">Select Month</InputLabel>
          <Select
            labelId="month-select-label"
            value={selectedMonth}
            label="Select Month"
            onChange={(e) => setSelectedMonth(e.target.value)}
          >
            {months.map((month, index) => (
              <MenuItem key={index} value={month}>
                {month}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Box
          sx={{
            backgroundColor: '#F6F6F6',
            padding: '12px',
            borderRadius: '8px',
            textAlign: 'center',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <CalendarTodayIcon sx={{ mr: 1 }} />
          <Typography variant="body1" sx={{ fontSize: '1rem' }}>
            {currentDate}
          </Typography>
        </Box>
      </Box>

      {salaryData && (
        <Box
          sx={{
            backgroundColor: 'white',
            borderRadius: 2,
            padding: 3,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              mb: 2,
            }}
          >
            {/* Total Salary */}
            <Typography variant="h5">Gross Salary: PKR {salaryData.grossPay.toFixed(2)}</Typography>

            {/* Print button */}
            <Button
              variant="contained"
              sx={{
                ...buttonStyles,
                mt: 0,
                height: '40px',
                whiteSpace: 'nowrap',
                padding: '0 16px',
                minWidth: '120px',
              }}
              onClick={handlePrintDialog}
            >
              Print
            </Button>
          </Box>

          <Divider sx={{ marginBottom: 2, height: 2, width: '100%', backgroundColor: '#39B54A' }} />

          {/* Salary Breakdown */}
          <Typography variant="h6" sx={{ marginBottom: 2 }}>
            Salary Breakdown for {salaryData.month}
          </Typography>

          {/* SalarySlip Component */}
          <SalarySlip salaryData={salaryData} user={user} />
        </Box>
      )}

      {/* Dialog for Printing Salary Slip */}
      <Dialog open={openDialog} onClose={handleCloseDialog} fullWidth maxWidth="md">
        <DialogContent>
          <SalarySlip salaryData={salaryData} user={user} />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} color="inherit" sx={{ color: 'red' }} className="no-print">Close</Button>
          <Button onClick={handlePrint} sx={{...buttonStyles, mt:0}} variant="contained" className="no-print">Print</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ESalary;
